function throttle(fn, delay = 100) {
  let lastCall = 0;
  return function(...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) return;
    lastCall = now;
    return fn(...args);
  };
}

class SplitLines extends HTMLElement {
  constructor() {
    super();
    this._splitDone = false;
    this._lastScreenWidth = window.innerWidth;
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(document.createRange().createContextualFragment("<slot></slot>"));
    window.addEventListener("resize", throttle(this._onWindowResized.bind(this)));
    new MutationObserver(this.split.bind(this, true)).observe(this, { characterData: true, attributes: false, childList: false, subtree: true });
  }

  connectedCallback() {
    this.split();
  }

  get lines() {
    return Array.from(this.shadowRoot.children);
  }

  split(force = false) {
    if (this._splitDone && !force) {
      return;
    }
    const text = this.textContent.trim();
    if (!text) return;

    this.shadowRoot.innerHTML = text.replace(/./g, "<span>$&</span>").replace(/\s/g, " ");
    const bounds = new Map();
    Array.from(this.shadowRoot.children).forEach((letter) => {
      const key = Math.round(letter.getBoundingClientRect().top);
      bounds.set(key, (bounds.get(key) || "").concat(letter.textContent));
    });
    this.shadowRoot.replaceChildren(...Array.from(bounds.values(), (line) => {
      const frag = document.createRange().createContextualFragment(`
        <span style="display: inline-block; vertical-align: top; overflow: hidden;">
          <span style="display: block">${line}</span>
        </span>
      `);
      return frag;
    }));
    this._splitDone = true;
  }

  _onWindowResized() {
    if (this._lastScreenWidth === window.innerWidth) {
      return;
    }
    this.split(true);
    this._lastScreenWidth = window.innerWidth;
  }
}

function animateHeading(headingEl, apparition, duration = 300) {
  if (!headingEl) return [];
  const splitLinesEl = headingEl.querySelector('split-lines');
  if (splitLinesEl) {
    splitLinesEl.split(true); // Force split so lines are calculated with actual layout width
  }
  const splitLines = splitLinesEl ? splitLinesEl.lines : null;

  headingEl.style.opacity = '1';

  let animations = [];
  if (apparition === 'fade' || !splitLines || splitLines.length === 0) {
    const anim = headingEl.animate([
      { opacity: 0 },
      { opacity: 1 }
    ], {
      duration: 200,
      easing: 'ease-out',
      fill: 'forwards'
    });
    animations.push(anim);
  } else {
    if (apparition === 'split_fade') {
      splitLines.forEach((line, index) => {
        const anim = line.animate([
          { transform: 'translateY(0.5em)', opacity: 0 },
          { transform: 'translateY(0)', opacity: 1 }
        ], {
          duration: 300,
          delay: index * 100,
          easing: 'ease-out',
          fill: 'forwards'
        });
        animations.push(anim);
      });
    } else if (apparition === 'split_clip') {
      splitLines.forEach((line, index) => {
        const anim = line.animate([
          { clipPath: 'inset(0 0 100% 0)', transform: 'translateY(100%)', opacity: 0 },
          { clipPath: 'inset(0 0 -0.3em 0)', transform: 'translateY(0)', opacity: 1 }
        ], {
          duration: 700,
          delay: index * 150,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'forwards'
        });
        animations.push(anim);
      });
    } else if (apparition === 'split_rotation') {
      splitLines.forEach((line, index) => {
        const innerSpan = line.querySelector('span');
        if (innerSpan) {
          innerSpan.style.transformOrigin = 'top left';
          line.style.clipPath = 'inset(0 0 -0.3em 0)';
          const anim = innerSpan.animate([
            { transform: 'translateY(0.5em) rotate(5deg)', opacity: 0 },
            { transform: 'translateY(0) rotate(0)', opacity: 1 }
          ], {
            duration: 400,
            delay: index * 100,
            easing: 'ease-out',
            fill: 'forwards'
          });
          animations.push(anim);
        }
      });
    }
  }
  return animations;
}

class SlideshowCarousel extends HTMLElement {
  constructor() {
    super();
    this.slides = Array.from(this.querySelectorAll('.slideshow__slide'));
    this.currentIndex = 0;
    this.autoplay = this.hasAttribute('autoplay');
    this.autoplaySpeed = parseFloat(this.getAttribute('autoplay')) * 1000 || 5000;
    this.timer = null;
    this.activeAnimations = [];
    this.contentAnimations = [];
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  init() {
    if (this.slides.length === 0) return;

    // Show first slide
    this.showSlide(0);

    // Bind controls (prev / next buttons)
    const prevBtn = this.querySelector('.prev-button');
    const nextBtn = this.querySelector('.next-button');
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.previous();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.next();
      });
    }

    // Bind page dots
    const dots = Array.from(this.querySelectorAll('.stretching-dots button, .numbered-dots button'));
    dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.showSlide(index);
      });
    });

    // Bind click / tap to slideshow when custom cursor is active
    if (this.querySelector('.slideshow__cursor')) {
      this.addEventListener('click', (e) => {
        // Avoid triggering when clicking links/buttons
        if (e.target.matches('button, a, button *, a *') || !window.matchMedia('screen and (pointer: fine)').matches) {
          return;
        }
        const rect = this.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        if (clickX > rect.width / 2) {
          this.next();
        } else {
          this.previous();
        }
      });
    }

    // Autoplay
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    this.animateCircularProgress();
    this.timer = setInterval(() => {
      this.next();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.activeAnimations) {
      this.activeAnimations.forEach(animation => animation.cancel());
      this.activeAnimations = [];
    }
    this.querySelectorAll('.numbered-dots button circle:last-child').forEach(circle => {
      circle.style.strokeDasharray = '';
    });
  }

  previous() {
    let index = this.currentIndex - 1;
    if (index < 0) index = this.slides.length - 1;
    this.showSlide(index);
  }

  next() {
    let index = this.currentIndex + 1;
    if (index >= this.slides.length) index = 0;
    this.showSlide(index);
  }

  showSlide(index) {
    const oldIndex = this.currentIndex;
    this.currentIndex = index;

    // Get transition type
    const transition = this.getAttribute('transition') || 'fade_with_text';
    const headingApparition = this.getAttribute('heading-apparition') || 'split_rotation';

    // Cancel all active content animations
    if (this.contentAnimations) {
      this.contentAnimations.forEach(anim => {
        try { anim.cancel(); } catch(e) {}
      });
      this.contentAnimations = [];
    }

    const oldSlide = this.slides[oldIndex];
    const newSlide = this.slides[index];

    // Fade out old slide if different from new slide
    if (oldSlide && oldSlide !== newSlide) {
      oldSlide.classList.remove('is-selected');
      oldSlide.setAttribute('aria-hidden', 'true');
      const fadeOut = oldSlide.animate([
        { opacity: 1, visibility: 'visible' },
        { opacity: 0, visibility: 'hidden' }
      ], {
        duration: 300,
        easing: 'ease-in-out',
        fill: 'forwards'
      });
      this.contentAnimations.push(fadeOut);
    }

    // Set classes and attributes for new slide
    newSlide.classList.add('is-selected');
    newSlide.setAttribute('aria-hidden', 'false');

    // Fade in new slide
    const fadeIn = newSlide.animate([
      { opacity: 0, visibility: 'hidden' },
      { opacity: 1, visibility: 'visible' }
    ], {
      duration: 300,
      easing: 'ease-in-out',
      fill: 'forwards'
    });
    this.contentAnimations.push(fadeIn);

    // If transition type is fade_with_text, run sequential animations
    if (transition === 'fade_with_text') {
      // Zoom/Scale in the slide images
      const imgs = newSlide.querySelectorAll('img');
      imgs.forEach(img => {
        const imgAnim = img.animate([
          { transform: 'scale(1.05)', opacity: 0 },
          { transform: 'scale(1)', opacity: 1 }
        ], {
          duration: 400,
          easing: 'ease-out',
          fill: 'forwards'
        });
        this.contentAnimations.push(imgAnim);
      });

      // Staggered text animations
      const subheading = newSlide.querySelector('[data-sequence="subheading"]');
      if (subheading) {
        const subAnim = subheading.animate([
          { opacity: 0, transform: 'translateY(10px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], {
          duration: 300,
          delay: 100,
          easing: 'ease-out',
          fill: 'forwards'
        });
        this.contentAnimations.push(subAnim);
      }

      const heading = newSlide.querySelector('[data-sequence="heading"]');
      if (heading) {
        const headingAnims = animateHeading(heading, headingApparition, 300);
        this.contentAnimations = this.contentAnimations.concat(headingAnims);
      }

      const button = newSlide.querySelector('.button, [class*="button--"]');
      if (button) {
        const btnAnim = button.animate([
          { opacity: 0, transform: 'translateY(10px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], {
          duration: 300,
          delay: 200,
          easing: 'ease-out',
          fill: 'forwards'
        });
        this.contentAnimations.push(btnAnim);
      }
    } else {
      // Fallback/standard transition: ensure everything is visible
      const imgs = newSlide.querySelectorAll('img');
      imgs.forEach(img => {
        img.style.opacity = '1';
        img.style.transform = 'scale(1)';
      });
      const subheading = newSlide.querySelector('[data-sequence="subheading"]');
      if (subheading) subheading.style.opacity = '1';
      const heading = newSlide.querySelector('[data-sequence="heading"]');
      if (heading) heading.style.opacity = '1';
      const button = newSlide.querySelector('.button, [class*="button--"]');
      if (button) button.style.opacity = '1';
    }

    // Update pagination controls
    const dots = Array.from(this.querySelectorAll('.stretching-dots button, .numbered-dots button'));
    dots.forEach((dot, idx) => {
      if (idx === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    // Dispatch selection event
    const event = new CustomEvent('carousel:select', {
      detail: { slide: this.slides[index], index: index },
      bubbles: true
    });
    this.dispatchEvent(event);

    // If autoplay is active, reset timer
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  animateCircularProgress() {
    // Stop any existing WAAPI animations
    if (this.activeAnimations) {
      this.activeAnimations.forEach(animation => animation.cancel());
    }
    this.activeAnimations = [];

    // Update cursor ring progress if present (look for cursor in parent x-slideshow)
    const cursorCircle = this.closest('x-slideshow')?.querySelector('.slideshow__cursor-ring circle') || this.querySelector('.slideshow__cursor-ring circle');
    if (cursorCircle) {
      const length = cursorCircle.getTotalLength() || 169.646;
      const animation = cursorCircle.animate([
        { strokeDasharray: `0px, ${length}px` },
        { strokeDasharray: `${length}px, ${length}px` }
      ], {
        duration: this.autoplaySpeed,
        easing: 'linear',
        fill: 'forwards'
      });
      this.activeAnimations.push(animation);
    }

    const activeDot = this.querySelector('.numbered-dots button[aria-current="true"]');
    if (!activeDot) return;

    // Reset stroke-dasharray on all dots first
    this.querySelectorAll('.numbered-dots button').forEach(dot => {
      const circle = dot.querySelector('circle:last-child');
      if (circle) {
        circle.style.strokeDasharray = '';
      }
    });

    const circle = activeDot.querySelector('circle:last-child');
    if (circle) {
      const length = circle.getTotalLength() || 94.247;
      const animation = circle.animate([
        { strokeDasharray: `0px, ${length}px` },
        { strokeDasharray: `${length}px, ${length}px` }
      ], {
        duration: this.autoplaySpeed,
        easing: 'linear',
        fill: 'forwards'
      });
      this.activeAnimations.push(animation);
    }
  }
}

class XSlideshow extends HTMLElement {
  connectedCallback() {
    this.addEventListener('carousel:select', (e) => {
      const slide = e.detail.slide;
      const slideStyles = getComputedStyle(slide);
      this.style.setProperty('--slideshow-controls-background', slideStyles.getPropertyValue('--slideshow-slide-controls-background'));
      this.style.setProperty('--slideshow-controls-color', slideStyles.getPropertyValue('--slideshow-slide-controls-color'));

      // If boxed, update slide background
      if (this.classList.contains('slideshow--boxed')) {
        const backgroundElement = this.querySelector('.slideshow__slide-background');
        const background = slideStyles.getPropertyValue('--slideshow-slide-background');
        if (backgroundElement) {
          backgroundElement.style.background = background;
          backgroundElement.style.opacity = '1';
        }
        this.style.setProperty('--slideshow-background', background);
      }
    });
  }
}

class CustomCursor extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
  }

  connectedCallback() {
    this._abortController = new AbortController();
    
    // Custom cursor follows mouse within the slideshow carousel container
    const container = this.closest('slideshow-carousel') || this.parentElement;
    
    container.addEventListener('pointermove', this._onPointerMove.bind(this), { passive: true, signal: this._abortController.signal });
    container.addEventListener('pointerleave', this._onPointerLeave.bind(this), { signal: this._abortController.signal });
  }

  disconnectedCallback() {
    if (this._abortController) {
      this._abortController.abort();
    }
  }

  _onPointerLeave() {
    this.classList.remove('is-visible', 'is-half-start', 'is-half-end');
  }

  _onPointerMove(event) {
    // Hide custom cursor when hovering over interactive elements
    if (event.target.matches('button, a, button *, a *')) {
      return this.classList.remove('is-visible');
    }

    const parentBoundingRect = this.parentElement.getBoundingClientRect();
    const parentXCenter = (parentBoundingRect.left + parentBoundingRect.right) / 2;
    const isOnStartHalfPart = event.clientX < parentXCenter;

    this.classList.toggle('is-half-start', isOnStartHalfPart);
    this.classList.toggle('is-half-end', !isOnStartHalfPart);
    this.classList.add('is-visible');

    const mouseY = event.clientY - parentBoundingRect.top - this.clientHeight / 2;
    const mouseX = event.clientX - parentBoundingRect.left - this.clientWidth / 2;

    this.style.transform = `translate3d(${mouseX.toFixed(1)}px, ${mouseY.toFixed(1)}px, 0)`;
  }
}

if (!customElements.get('slideshow-carousel')) {
  customElements.define('slideshow-carousel', SlideshowCarousel);
}
if (!customElements.get('x-slideshow')) {
  customElements.define('x-slideshow', XSlideshow);
}
if (!customElements.get('custom-cursor')) {
  customElements.define('custom-cursor', CustomCursor);
}
if (!customElements.get('split-lines')) {
  customElements.define('split-lines', SplitLines);
}
